import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import * as authAction from "state/auth/authAction";
import {
  StandardButton,
  StandardInput,
  Icon,
  StandardDropdown
} from "@trackcode/ui";
import CI from "@trackcode/ci";
import logoTrackCode from "asset/image/trackcode-logo.png";
import {
  hasDemoModeAccess,
  hasWencoDemoModeAccess,
  isDemoModeEnabled,
  isWencoDemoModeEnabled,
  setDemoModeEnabled,
  setWencoDemoModeEnabled
} from "common/demoModeControls";
import {
  OnDarkButton,
  OnDarkDropdown,
  PlatformAppBarShell,
  PlatformAppBarUI,
  PlatformHeaderCtaButton
} from "features/app/components/platformHeaderStyles";
import { getLogisticCompanySwitcherState } from "features/app/components/logisticCompanySwitcherUtils";

class AppBar extends Component {
  static propTypes = {
    handleCreateShipment: PropTypes.func.isRequired,
    handleMenuItemClicked: PropTypes.func.isRequired,
    shipmentButtonDisabled: PropTypes.bool.isRequired,
    user: PropTypes.shape({
      id: PropTypes.number,
      firstname: PropTypes.string,
      lastname: PropTypes.string
    }),
    logisticCompanyId: PropTypes.number.isRequired,
    managedLogisticCompanies: PropTypes.shape({
      id: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    }).isRequired,
    logoutAction: PropTypes.func.isRequired,
    changeLogisticCompanyAction: PropTypes.func.isRequired,
    sidebarOffset: PropTypes.number,
    showSettingsButton: PropTypes.bool,
    showProfileButton: PropTypes.bool,
    showLogo: PropTypes.bool,
    showTrackingSearch: PropTypes.bool,
    showCreateShipmentButton: PropTypes.bool,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired
    }).isRequired
  };

  static defaultProps = {
    user: null,
    sidebarOffset: 0,
    showSettingsButton: true,
    showProfileButton: true,
    showLogo: true,
    showTrackingSearch: true,
    showCreateShipmentButton: true
  };

  state = {
    tracking: "",
    showMenu: false,
    demoModeEnabled: false,
    wencoDemoModeEnabled: false
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.pointerDownListener);
    this.setState({
      demoModeEnabled: isDemoModeEnabled(),
      wencoDemoModeEnabled: isWencoDemoModeEnabled()
    });
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.pointerDownListener);
  }

  /**
   * Change tracking text.
   */
  onChangeTracking = e => {
    this.setState({ tracking: e.target.value });
  };

  setAppBarRef = ref => {
    this.appBarRef = ref;
  };

  pointerDownListener = event => {
    const { showMenu } = this.state;

    if (!showMenu) {
      return;
    }

    const isInside = this.appBarRef
      ? this.appBarRef.contains(event.target)
      : false;

    if (!isInside) {
      this.setState({ showMenu: false });
    }
  };

  toggleMenu = event => {
    if (event) {
      event.stopPropagation();
    }

    this.setState(prevState => ({ showMenu: !prevState.showMenu }));
  };

  handleTracking = e => {
    const { history } = this.props;
    const { tracking } = this.state;
    if (tracking.length < 2) {
      return;
    }

    // catch onClick and onKeyPress
    if (!e.key || (e.key && e.key === "Enter")) {
      history.push(`/track/${tracking}`);
      // Clear input after navigation with a small delay
      setTimeout(() => {
        this.setState({ tracking: "" });
      }, 100);
    }
  };

  handleCreateShipment = () => {
    this.props.handleCreateShipment();
  };

  handleSetting = () => {
    this.props.handleMenuItemClicked("settings");
  };

  handleLogout = () => {
    this.props.logoutAction();
  };

  toggleDemoMode = () => {
    const nextEnabled = !this.state.demoModeEnabled;
    setDemoModeEnabled(nextEnabled);
    this.setState({ demoModeEnabled: nextEnabled });
  };

  toggleWencoDemoMode = () => {
    const nextEnabled = !this.state.wencoDemoModeEnabled;
    setWencoDemoModeEnabled(nextEnabled);
    this.setState({ wencoDemoModeEnabled: nextEnabled });
  };

  changeLogisticCompany = id => () => {
    const {
      logisticCompanyId,
      changeLogisticCompanyAction,
      history
    } = this.props;
    // const newId = id === defaultId ? 0 : id;
    const newId = id;

    if (newId !== logisticCompanyId) {
      changeLogisticCompanyAction(newId);
      history.push(`/scheduling`);
    }
    this.setState({ showMenu: false });
  };

  render() {
    const {
      shipmentButtonDisabled,
      user,
      logisticCompanyId,
      managedLogisticCompanies,
      sidebarOffset,
      showSettingsButton,
      showProfileButton,
      showLogo,
      showTrackingSearch,
      showCreateShipmentButton,
      history
    } = this.props;
    const { showMenu, tracking, demoModeEnabled, wencoDemoModeEnabled } =
      this.state;

    const switcherState = getLogisticCompanySwitcherState({
      logisticCompanyId,
      user,
      managedLogisticCompanies
    });
    const {
      showSwitcher: showCurrentLogisticcompany,
      activeLogisticCompanyId: currLogisticcompanyId,
      menuItems: logisticCompanyMenuItems,
      currentLabel: currentLogisticCompanyLabel,
      resolveMenuItemLabel
    } = switcherState;
    const hasDemoModeAccessFlag = hasDemoModeAccess(managedLogisticCompanies);
    const hasWencoDemoAccess = hasWencoDemoModeAccess(managedLogisticCompanies);

    const isOnDark = !showMenu;
    const SubtleButton = isOnDark ? OnDarkButton : StandardButton;
    const UserDropdown = isOnDark ? OnDarkDropdown : StandardDropdown;

    return (
      <Header role="navigation" $sidebarOffset={sidebarOffset}>
        <PlatformAppBarShell ref={this.setAppBarRef} $onDark={isOnDark}>
          <PlatformAppBarUI
            showMenu={showMenu}
            $menuOpen={showMenu}
          logo={
            showLogo ? (
              <img
                src={logoTrackCode}
                alt="TrackCode Logo"
                style={{
                  height: "32px",
                  padding: "5px 10px",
                  cursor: showCurrentLogisticcompany ? "auto" : "pointer"
                }}
                onClick={
                  showCurrentLogisticcompany
                    ? () => {}
                    : () => {
                        history.push("/scheduling");
                      }
                }
              />
            ) : null
          }
          left={
            showCurrentLogisticcompany ? (
              <Fragment>
                <SubtleButton
                  // set key for an smooth button switch
                  key="menu-close"
                  appearance="subtle"
                  style={{
                    // use display because of clickListener inside check
                    display: showMenu ? "block" : "none"
                  }}
                  onClick={this.toggleMenu}
                >
                  <Icon name="close" size="md" />
                </SubtleButton>

                <SubtleButton
                  // set key for an smooth button switch
                  key="menu-open"
                  appearance="subtle"
                  iconBefore={{
                    name: "menu",
                    width: "21",
                    height: "21"
                  }}
                  textWrap="ellipsis"
                  style={{
                    // use display because of clickListener inside check
                    display: showMenu ? "none" : "block",
                    maxWidth: "100%",
                    fontSize: "15px",
                    fontWeight: "700"
                  }}
                  onClick={this.toggleMenu}
                >
                  {currentLogisticCompanyLabel}
                </SubtleButton>
              </Fragment>
            ) : currentLogisticCompanyLabel ? (
              <LogisticCompanyLabel
                $onDark={isOnDark}
                title={currentLogisticCompanyLabel}
              >
                {currentLogisticCompanyLabel}
              </LogisticCompanyLabel>
            ) : null
          }
          center={
            showTrackingSearch ? (
            <Fragment>
              <StandardInput
                placeholder="Sendungsnummer eingeben"
                spaceAfter={false}
                style={{
                  width: "300px",
                  borderBottomRightRadius: 0,
                  borderTopRightRadius: 0,
                  ...(isOnDark
                    ? {
                        backgroundColor: "#fff",
                        borderColor: "rgba(255, 255, 255, 0.9)"
                      }
                    : {})
                }}
                value={tracking}
                onChange={this.onChangeTracking}
                onKeyPress={this.handleTracking}
              />
              <StandardButton
                style={{
                  borderBottomLeftRadius: 0,
                  borderTopLeftRadius: 0,
                  borderLeftWidth: 0,
                  ...(isOnDark
                    ? {
                        backgroundColor: "#fff",
                        borderColor: "rgba(255, 255, 255, 0.9)",
                        color: CI.color.grayDark
                      }
                    : {})
                }}
                onClick={this.handleTracking}
              >
                <Icon name="search" size="md" />
              </StandardButton>
            </Fragment>
            ) : null
          }
          right={
            <Fragment>
              {showCreateShipmentButton ? (
                isOnDark ? (
                  <PlatformHeaderCtaButton
                    disabled={shipmentButtonDisabled}
                    style={{ whiteSpace: "nowrap" }}
                    onClick={this.handleCreateShipment}
                  >
                    Auftrag hinzufügen
                  </PlatformHeaderCtaButton>
                ) : (
                  <StandardButton
                    disabled={shipmentButtonDisabled}
                    appearance={shipmentButtonDisabled ? "default" : "primary"}
                    style={{ whiteSpace: "nowrap" }}
                    onClick={this.handleCreateShipment}
                  >
                    Auftrag hinzufügen
                  </StandardButton>
                )
              ) : null}
              {showSettingsButton ? (
                <SubtleButton
                  appearance="subtle"
                  spaceAfter={false}
                  onClick={this.handleSetting}
                >
                  <Icon name="setting" size="md" />
                </SubtleButton>
              ) : null}
            </Fragment>
          }
          user={
            showProfileButton &&
            user && (
              <UserDropdown
                appearance="subtle"
                justifyContent="flex-start"
                shouldFitContainer
                textWrap="ellipsis"
                iconAfter={{ name: "angle-down" }}
                buttonChildren={`${user.firstname} ${user.lastname}`}
              >
                {Item => [
                  hasDemoModeAccessFlag && (
                    <Item key="demo-mode" onClick={this.toggleDemoMode}>
                      {`Demo-Modus ${demoModeEnabled ? "deaktivieren" : "aktivieren"}`}
                    </Item>
                  ),
                  hasWencoDemoAccess && (
                    <Item key="wenco-demo-mode" onClick={this.toggleWencoDemoMode}>
                      {`DLB-Demo ${wencoDemoModeEnabled ? "deaktivieren" : "aktivieren"}`}
                    </Item>
                  ),
                  <Item key="logout" onClick={this.handleLogout}>
                    Abmelden
                  </Item>
                ]}
              </UserDropdown>
            )
          }
          menu={
            <Menu>
              {logisticCompanyMenuItems.map(({ id, name }) => (
                <MenuItem key={id}>
                  <StandardButton
                    appearance="subtle"
                    shouldFitContainer
                    justifyContent="flex-start"
                    textWrap="ellipsis"
                    style={{
                      color:
                        currLogisticcompanyId === id
                          ? CI.color.brand
                          : CI.color.grayDark,
                      fontWeight:
                        currLogisticcompanyId === id ? "bold" : "normal"
                    }}
                    onClick={this.changeLogisticCompany(id)}
                  >
                    {resolveMenuItemLabel(id, name)}
                  </StandardButton>
                </MenuItem>
              ))}
            </Menu>
          }
        />
        </PlatformAppBarShell>
      </Header>
    );
  }
}

const mapStateToProps = ({
  auth: { user, logisticCompanyId = 0, managedLogisticCompanies = {} }
}) => ({
  user,
  logisticCompanyId,
  managedLogisticCompanies
});

const mapDispatchToProps = {
  logoutAction: authAction.logout,
  changeLogisticCompanyAction: authAction.changeLogisticCompany
};

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(AppBar)
);

const Header = styled.header`
  position: fixed;
  top: 0;
  left: ${({ $sidebarOffset }) => `${$sidebarOffset}px`};
  width: ${({ $sidebarOffset }) => `calc(100% - ${$sidebarOffset}px)`};
  /* more z-index than the old modal */
  z-index: 2000;
  transition: left 0.32s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const LogisticCompanyLabel = styled.span`
  display: block;
  max-width: 100%;
  /* Align with the collaboration ("Zusammenarbeit") header, where the company
     name sits at a 32px content inset. The AppBar's Menu container already adds
     14px (CI.space.sm) of left padding, so 18px here yields the same 32px. */
  padding: 0 8px 0 26px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $onDark }) => ($onDark ? "#fff" : CI.color.grayDark)};
`;

const Menu = styled.div`
  display: flex;
  width: 840px;
  flex-wrap: wrap;
  justify-content: flex-start;
`;
const MenuItem = styled.div`
  min-width: 280px;
  max-width: 280px;
  padding-bottom: 6px;
`;
